import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./AddArticle.css";
import { toast } from "react-toastify";
import { createArticle } from "../../../../services/Article/Article.service";
import { getCategoriess } from "../../../../services/Category/category.service";
import { getAllDesireds } from "../../../../services/DesiredType/DesiredType.service";

const AddArticle: React.FC = () => {
  const navigate = useNavigate();

  const [articleName, setArticleName] = useState("");
  const [description, setDescription] = useState("");
  const [metalTypeId, setMetalTypeId] = useState<number | "">("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [metalTypes, setMetalTypes] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchDropdowns = async () => {
    try {
      setLoading(true);
      
      // Fetch both APIs in parallel
      const [metals, cats] = await Promise.all([
        getAllDesireds(),
        getCategoriess()
      ]);

      // Set metal types
      setMetalTypes(metals?.data || []);
      
      // The backend already filters for admin categories (createdByRole: "admin")
      // But we'll also filter on frontend for safety
      const adminCategories = (cats || []).filter(
        (item) => item.createdByRole?.toLowerCase() === "admin"
      );
      
      setCategories(adminCategories);
      
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
      toast.error("Failed to load dropdown data");
    } finally {
      setLoading(false);
    }
  };

  // Filter category by Metal Type + Search
  const filteredCategories = categories.filter(
    (item) =>
      Number(item.metalTypeId) === Number(metalTypeId) &&
      item.name?.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!articleName.trim()) {
      toast.error("Article name is required");
      return;
    }

    if (!metalTypeId) {
      toast.error("Please select a metal type");
      return;
    }

    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      await createArticle({
        articleName: articleName.trim(),
        description: description.trim(),
        metalTypeId: Number(metalTypeId),
        categoryId: Number(categoryId),
      });

      toast.success("Article created successfully");
      navigate("/article/list");
    } catch (error: any) {
      console.error("Error creating article:", error);
      toast.error(error?.response?.data?.message || "Failed to create article");
    }
  };

  return (
    <div className="add-article-overlay">
      <div className="add-article-container">
        <button
          type="button"
          className="close-btn"
          onClick={() => navigate("/article/list")}
        >
          &times;
        </button>

        <h2>Add Article</h2>

        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Article Name */}
            <input
              type="text"
              placeholder="Article Name"
              value={articleName}
              onChange={(e) => setArticleName(e.target.value)}
              required
            />

            {/* Metal Type */}
            <select
              value={metalTypeId}
              onChange={(e) => {
                const selectedMetal = Number(e.target.value);
                setMetalTypeId(selectedMetal);
                setCategoryId("");
                setCategorySearch("");
                setShowDropdown(false);
              }}
              required
            >
              <option value="">Select Metal Type</option>
              {metalTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            {/* Searchable Category - Only Admin Categories */}
            <div className="searchable-dropdown" ref={dropdownRef}>
              <input
                type="text"
                disabled={!metalTypeId}
                placeholder={
                  metalTypeId
                    ? "Search Category"
                    : "Select Metal Type First"
                }
                value={categorySearch}
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => {
                  if (metalTypeId && categories.length > 0) {
                    setShowDropdown(true);
                  }
                }}
                required
              />

              {showDropdown && metalTypeId && (
                <div className="dropdown-list">
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((item) => (
                      <div
                        key={item.id}
                        className="dropdown-item"
                        onClick={() => {
                          setCategoryId(item.id);
                          setCategorySearch(item.name);
                          setShowDropdown(false);
                        }}
                      >
                        <div className="category-item-content">
                          <span className="category-name">{item.name}</span>
                       
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item no-results">
                      {categories.length === 0 
                        ? "No admin categories available" 
                        : "No matching categories found"}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            <textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />

            <button type="submit">Create Article</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddArticle;